import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { resolveAssetUrl } from "@/contexts/SiteContentContext";
import AdminShell from "@/components/admin/AdminShell";
import ImageUploader from "@/components/admin/ImageUploader";
import { Trash2, Copy, Loader2 } from "lucide-react";

const AdminMedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/uploads");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (item) => {
    if (!window.confirm(`Bild "${item.filename}" löschen?`)) return;
    try {
      await api.delete(`/admin/uploads/${item.filename}`);
      setItems((xs) => xs.filter((x) => x.filename !== item.filename));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const copyUrl = async (url) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("URL kopiert");
        return;
      }
      throw new Error("Clipboard API nicht verfügbar");
    } catch {
      // Fallback: hidden textarea + execCommand
      try {
        const el = document.createElement("textarea");
        el.value = url;
        el.setAttribute("readonly", "");
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        toast.success("URL kopiert");
      } catch {
        toast.info("Bitte URL manuell kopieren");
        window.prompt("URL:", url);
      }
    }
  };

  return (
    <AdminShell
      title="Mediathek"
      subtitle="Lade Bilder hoch und nutze sie überall in der Website."
    >
      <div className="mb-8 rounded-3xl bg-white border border-neutral-200 p-6">
        <ImageUploader
          value=""
          onChange={() => load()}
          label="Neues Bild hochladen"
          testId="media-upload"
        />
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center">
          <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-neutral-500 rounded-3xl bg-white border border-neutral-200" data-testid="media-empty">
          Noch keine Bilder hochgeladen.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {items.map((item) => (
            <div
              key={item.filename}
              className="group rounded-2xl border border-neutral-200 bg-white overflow-hidden"
              data-testid={`media-item-${item.filename}`}
            >
              <div className="aspect-square bg-neutral-100 overflow-hidden">
                <img
                  src={resolveAssetUrl(item.url)}
                  alt={item.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <div className="text-[10px] text-neutral-500 truncate font-mono">
                  {item.filename}
                </div>
                <div className="text-[10px] text-neutral-400">
                  {(item.size / 1024).toFixed(0)} KB
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    onClick={() => copyUrl(item.url)}
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-neutral-100 hover:bg-neutral-200"
                    data-testid={`media-copy-${item.filename}`}
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </button>
                  <button
                    onClick={() => onDelete(item)}
                    className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full text-red-600 hover:bg-red-50"
                    data-testid={`media-delete-${item.filename}`}
                  >
                    <Trash2 className="w-3 h-3" />
                    Del
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
};

export default AdminMedia;
